/**
 * Срок проверки актуальности статьи («Проверять каждые…»): готовые варианты,
 * свой интервал и подсказка про напоминание. Правила совпадают с бэкендом
 * (`backend/src/modules/wiki/wiki-owner.ts`): целое 1…1095 дней или «не проверять».
 */

/** Верхняя граница своего интервала (3 года) — как на бэкенде. */
export const MAX_VERIFY_DAYS = 1095;

/** Готовые варианты интервала в днях; всё остальное — «Свой интервал». */
export const VERIFY_PRESETS: readonly number[] = [15, 30, 60, 90, 180, 365];

/** Значения Select: «Не проверять» и «Свой интервал…» (числа дней в него не попадают). */
export const VERIFY_NONE = 0;
export const VERIFY_CUSTOM = -1;

/** Подписи готовых вариантов в списке. */
export function verifyPresetLabel(days: number): string {
  return days === 365 ? 'Раз в год' : `${days} дней`;
}

/** Целое число дней 1…1095. */
export function isValidVerifyEvery(days: unknown): days is number {
  return typeof days === 'number' && Number.isInteger(days) && days >= 1 && days <= MAX_VERIFY_DAYS;
}

/** Нестандартный интервал (например 45): в списке показываем «Свой интервал» с этим числом. */
export function isCustomVerifyEvery(days: number | null): boolean {
  return days != null && !VERIFY_PRESETS.includes(days);
}

/** Что показать в Select: «Не проверять», готовый вариант или «Свой интервал…». */
export function verifySelectValue(days: number | null, customMode: boolean): number {
  if (days == null && !customMode) return VERIFY_NONE;
  if (customMode || isCustomVerifyEvery(days)) return VERIFY_CUSTOM;
  return days as number;
}

/** Ввод числа дней в поле «Свой интервал»: пусто — null, иначе целое в пределах 1…1095. */
export function clampVerifyEvery(raw: number | string | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = Math.trunc(Number(raw));
  if (!Number.isFinite(n)) return null;
  return Math.min(MAX_VERIFY_DAYS, Math.max(1, n));
}

/**
 * За сколько дней до срока напомним владельцу: min(7, max(1, round(интервал / 4))).
 * Тот же расчёт, что `reminderSchedule` на бэкенде (там же «не чаще чем раз в N дней»).
 */
export function verifyReminderLead(days: number): number {
  return Math.min(7, Math.max(1, Math.round(days / 4)));
}

/** Склонение «день / дня / дней». */
export function daysWord(n: number): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return 'день';
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'дня';
  return 'дней';
}

/**
 * Подсказка под полем: «Напомним владельцу за 4 дня до срока». `customMode` — выбран «Свой
 * интервал…», но число ещё не введено: это не «Не проверять», а просьба ввести число.
 */
export function verifyReminderHint(days: number | null, customMode = false): string {
  if (days == null && !customMode) return 'Статья не попадёт в очередь проверки, напоминаний не будет.';
  if (!isValidVerifyEvery(days)) return `Укажите целое число дней от 1 до ${MAX_VERIFY_DAYS}.`;
  const n = verifyReminderLead(days);
  return `Напомним владельцу за ${n} ${daysWord(n)} до срока.`;
}

/** Подпись периодичности в карточке «Актуальность»: 180 дней читается как «раз в 6 месяцев». */
export function verifyPeriodLabel(days: number): string {
  if (days === 365) return 'раз в год';
  if (days === 180) return 'раз в 6 месяцев';
  if (days === 90) return 'раз в 3 месяца';
  return `раз в ${days} дн.`;
}
